import React, {ComponentType, useEffect, useState} from "react";
import EditBookingNotes from "./index";
import {IContainerProps, IServices} from './types';

import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import ReactUtilsService from "app/services/reactUtils.service";
import { IBooking, ICustomer } from "shared-types/SharedTypes";
import GeneralSettingsService from "external-services/general-settings-service/generalSettings.service";
import { IGeneralSettingsCache } from 'app/app.types';
import {cloneDeep} from 'lodash';
import UtilsService from "app/services/utils/utils.service";
import TelemetryService from "app/services/telemetry/telemetry.service";
import ManagerService from "external-services/manager-service/manager.service";
import CustomerUpdateService from "external-services/customer-update-service/customerUpdate.service";
import BookingLookupService from "external-services/booking-lookup-service/index";
import BookingUpdateService from "external-services/booking-update-service/index";
import CustomerLookupService from 'external-services/customer-lookup-service';

const NS = 'EditBookingNotesContainer';

const containerPropsKeys: string[] = keys<IContainerProps>();
const servicesKeys: string[] = keys<IServices>();

const ThemeWrap = function(all: IContainerProps & IServices) {

  const containerServices: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;
  const containerProps: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;

  const {showDialog = false, booking = null} = containerProps;
  const {handleClose, broadcast} = containerProps;

  const [bookingData, setBookingData] = useState<IBooking>(null);
  const [customerData, setCustomerData] = useState<ICustomer>(null);

  const [staffEditEnabled, setStaffEditEnabled] = useState<boolean>(false);

  const [staffError, setStaffError] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [loadCustomerError, setLoadCustomerError] = useState<boolean>(false);
  const [customerErrorMsg, setCustomerErrorMsg] = useState<string>('');

  const isManager = ManagerService.isManager;


  useEffect(() => {
    GeneralSettingsService.get().then((settings: IGeneralSettingsCache) => {
      const tempStaffEditEnabled = (settings.staffEditEnabled) ? true : false
      setStaffEditEnabled(tempStaffEditEnabled);
    });
  });

  useEffect(() => {
    if(showDialog && booking) {
      BookingLookupService.getById(booking._id, booking.time)
      .then((oldBooking: IBooking) => {
        const decodedNotes = UtilsService.htmlDecode(oldBooking.notes);
        setBookingData({...oldBooking, notes: decodedNotes});
      });

      if (booking.customerId) {
        CustomerLookupService.getById(booking.customerId).then((customer: ICustomer) => {
          if(customer) {
            setCustomerData(customer);
            setLoadCustomerError(false);
            setCustomerErrorMsg('');
          } else {
            setLoadCustomerError(true);
            setCustomerErrorMsg('There is no customer. You can\'t edit customer note');
            console.log("Error when verifying customer");
          }
        }).catch(() => {
          setLoadCustomerError(true);
          setCustomerErrorMsg('Error when verifying customer, You can\'t edit customer note');
          console.log("Error when verifying customer");
        });
      }
    } else setDefault();
  }, [showDialog]);

  const setDefault = () => {
    setBookingData(null);
    setCustomerData(null);
    setStaffError(false);
    setSaving(false);
    setSaveError(false);
    setErrorMsg('')  ;
    setLoadCustomerError(false);
    setCustomerErrorMsg('');
  }


  const strUtil = (str: string) => {
    if(!str) return "";
    else return str;
  }

  const updateBookingCustomerInfo = (booking: IBooking, customer: ICustomer): void => {
    if (customer && booking) {
      const hasCustomerNotes = customer.notes && customer.notes.length > 0;
      const hasManagerNotes = customer.managerNotes && customer.managerNotes.length > 0;

      if (booking.hasCustomerNotes !== hasCustomerNotes) {
        booking.hasCustomerNotes = hasCustomerNotes;
      }

      if (booking.hasManagerNotes !== hasManagerNotes) {
        booking.hasManagerNotes = hasManagerNotes;
      }
    }
  }

  const handleSave = (bookingNotes: string, customerNotes: string, managerNotes: string, staffName: string) => {
    const oldBooking = cloneDeep(bookingData);
    const editBooking = cloneDeep(bookingData);
    const editCustomer = cloneDeep(customerData);

    if(strUtil(bookingNotes) !== strUtil(bookingData.notes)) editBooking.notes = bookingNotes;

    let customerUpdated: boolean = false;
    if(customerData && strUtil(customerNotes) !== strUtil(customerData.notes)) {
      editCustomer.notes = customerNotes;
      customerUpdated = true;
    }
    if(customerData && strUtil(managerNotes) !== strUtil(customerData.managerNotes)) {
      editCustomer.managerNotes = managerNotes;
      customerUpdated = true;
    }

    updateBookingCustomerInfo(editBooking, editCustomer);

    let isSave = true;
    if(staffEditEnabled && !staffName) isSave = false;

    const t2 = performance.now();
    setStaffError(!isSave);

    if(isSave) {
      setSaving(true);
      BookingUpdateService.updateFromAnywhere(editBooking, oldBooking, null, null, staffName).then(() => {
        TelemetryService.track('updateBooking', [
          ['duration', performance.now() - t2]
        ]);
        if(customerUpdated) {
          CustomerUpdateService.update(editCustomer).then(() => {
            broadcast(editBooking);
            setTimeout(() => {
              handleClose();
            }, 1000);
          }).catch((err: any) => {
            broadcast(editBooking);
            setSaving(false);
            console.log('EditBooking.Update error', err);
            setErrorMsg('Failed to save customer note')
            setSaveError(true);
          });
        } else {
          broadcast(editBooking);
          setTimeout(() => {
            handleClose();
          }, 1000);
        }

      }).catch((err: any) => {
        setSaving(false);
        console.log('EditBooking.Update error', err);
        setErrorMsg('Failed to save booking note');
        setSaveError(true);
      });
    }
  }


  return ReactUtilsService.containerWrap(
    <EditBookingNotes
      showDialog={showDialog}
      saving={saving}
      saveError={saveError}
      errorMsg={errorMsg}
      loadCustomerError={loadCustomerError}
      customerErrorMsg={customerErrorMsg}
      booking={bookingData}
      customer={customerData}
      isManager={isManager}
      staffEditEnabled={staffEditEnabled}
      staffError={staffError}
      handleSave={handleSave}
      handleClose={() => handleClose()}
    />
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2AEditBookingNotes: [ComponentType<unknown>, never[]?,string[]?] = [
  hot(module)(ThemeWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[],
  servicesKeys
];
